import { useBookingCalendarStore } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/booking_calendar_store/booking_calendar_store_context";
import { useBookingModuleStoreActions, useCalendarRoot, useCalendarRootActions } from "PFApp/booking/store";
import { RowsWithRefs, RowWithRef } from "PFApp/booking/store";
import { Row } from "PFApp/booking/types";
import { RefObject, useRef } from "react";

import { DnDMode } from "../../../bars/booking_bar/use_booking_bar_dnd";
import { useBookingBarElement } from "../use_booking_bar_element";
import type { DragAxes } from "../use_drag_axes";
import { calculateDropZoneHeight } from "./calculate_drop_zone_height";
import { findClosestProfileRow } from "./find_closest_profile_row";
import { findRowWithRefAtClientY } from "./find_row_with_ref_at_client_y";
import { isDroppableTarget } from "./is_droppable_target";

type DataSnapshot = {
  rowsWithRefs: RowsWithRefs<Row>;
  rows: Row[];
  rowsByKey: Record<string, Row>;
};

const EMPTY_SNAPSHOT: DataSnapshot = {
  rowsWithRefs: new Map(),
  rows: [],
  rowsByKey: {}
};

type UseBookingDropZoneManager = {
  dropZoneRef: RefObject<HTMLDivElement | null>;
};

type UseBookingDropZoneManagerReturn = {
  onDragStart: () => void;
  onDragMove: (event: PointerEvent, axes: DragAxes) => void;
  onDragEnd: () => void;
};

export const useBookingDropZoneManager = ({
  dropZoneRef
}: UseBookingDropZoneManager): UseBookingDropZoneManagerReturn => {
  const calendarRoot = useCalendarRoot();
  const { getRowsWithRefs } = useCalendarRootActions(calendarRoot);
  const { getBookingBarElement } = useBookingBarElement();
  const {
    getBookingDragAndDropData,
    setBookingDragAndDropData,
    getCurrentRowsByKey,
    getRowsVisibleByBookingsVisibility
  } = useBookingModuleStoreActions();
  const rightPaneScrollableElement = useBookingCalendarStore((state) => state.rightPaneScrollableElement);

  const dataSnapshotRef = useRef<DataSnapshot>(EMPTY_SNAPSHOT);
  const shownRowKeyRef = useRef<string | null>(null);

  const hideDropZone = (): void => {
    const el = dropZoneRef.current;
    if (el) {
      el.style.display = "none";
    }
    shownRowKeyRef.current = null;
  };

  const updateDropZonePosition = (): void => {
    const el = dropZoneRef.current;
    const barEl = getBookingBarElement();
    if (!el || !barEl) {
      return;
    }
    el.style.left = barEl.style.left;
    el.style.width = barEl.style.width;
  };

  const showDropZone = (profileRowWithRef: RowWithRef<Row>): void => {
    const el = dropZoneRef.current;
    const barEl = getBookingBarElement();
    const containerRect = rightPaneScrollableElement?.getBoundingClientRect();

    if (!el || !barEl || !profileRowWithRef.rowRef || !containerRect) {
      return;
    }

    const { rows, rowsByKey, rowsWithRefs } = dataSnapshotRef.current;
    const profileRowRect = profileRowWithRef.rowRef.getBoundingClientRect();
    const height = calculateDropZoneHeight({
      targetRow: profileRowWithRef.row,
      rows,
      rowsByKey,
      rowsWithRefs
    });

    el.style.top = `${profileRowRect.top - containerRect.top}px`;
    el.style.left = barEl.style.left;
    el.style.width = barEl.style.width;
    el.style.height = `${height}px`;
    el.style.display = "block";
    shownRowKeyRef.current = profileRowWithRef.row.key;
  };

  const onDragStart = (): void => {
    dataSnapshotRef.current = {
      rowsWithRefs: getRowsWithRefs(),
      rows: getRowsVisibleByBookingsVisibility(),
      rowsByKey: getCurrentRowsByKey()
    };

    const { draggingMode, sourceRow } = getBookingDragAndDropData();
    if (draggingMode === DnDMode.HorizontalVertical && sourceRow) {
      const { rowsByKey } = dataSnapshotRef.current;
      setBookingDragAndDropData({
        dropZoneProfileRow: findClosestProfileRow({ row: sourceRow, rowsByKey }) ?? null
      });
    }
  };

  const findHoveredProfileRow = (event: PointerEvent): Row | null => {
    const { rowsByKey, rowsWithRefs } = dataSnapshotRef.current;
    const hoveredRowWithRef = findRowWithRefAtClientY(rowsWithRefs, event.clientY);

    return hoveredRowWithRef
      ? (findClosestProfileRow({ row: hoveredRowWithRef.row, rowsByKey }) ?? null)
      : null;
  };

  const onDragMove = (event: PointerEvent, axes: DragAxes): void => {
    const {
      draggingMode,
      sourceRow,
      dropZoneProfileRow: currentDropZoneProfileRow,
      targetRow: currentTargetRow
    } = getBookingDragAndDropData();
    const { rowsByKey, rowsWithRefs } = dataSnapshotRef.current;

    if (!sourceRow) {
      return;
    }

    const isReassignMode = draggingMode === DnDMode.HorizontalVertical;
    const profileRow = isReassignMode && axes.vertical ? findHoveredProfileRow(event) : null;
    const targetRow =
      profileRow && isDroppableTarget({ targetRow: profileRow, sourceRow, rowsByKey, calendarRoot })
        ? profileRow
        : null;
    const dropZoneProfileRow =
      targetRow ?? (isReassignMode ? findClosestProfileRow({ row: sourceRow, rowsByKey }) : undefined);

    if (dropZoneProfileRow?.key !== currentDropZoneProfileRow?.key) {
      setBookingDragAndDropData({ dropZoneProfileRow: dropZoneProfileRow ?? null });
    }
    if (targetRow?.key !== currentTargetRow?.key) {
      setBookingDragAndDropData({ targetRow });
    }

    const dropZoneRowWithRef = dropZoneProfileRow ? rowsWithRefs.get(dropZoneProfileRow.key) : undefined;

    if (!dropZoneRowWithRef) {
      hideDropZone();
    } else if (dropZoneRowWithRef.row.key === shownRowKeyRef.current) {
      updateDropZonePosition();
    } else {
      showDropZone(dropZoneRowWithRef);
    }
  };

  const onDragEnd = (): void => {
    hideDropZone();
    dataSnapshotRef.current = EMPTY_SNAPSHOT;
  };

  return { onDragStart, onDragMove, onDragEnd };
};
